<template>
  <Dropdown
    v-if="showDropdown"
    id="group-dropdown"
    :model-value="GroupLabel"
    :options="GroupLabels"
    class="mt-4 z-1"
    @update:model-value="itemService?.changeGroupLabel"
    :pt="{
      input: {
        class: 'text-xl sm:text-4xl md:text-6xl'
      },
      item: {
        class: 'text-xl sm:text-xl md:text-3xl'
      }
    }"
  />
  <ShareLink v-if="showDropdown" class="w-full flex justify-content-center z-1 mt-3" />

  <div ref="container" class="flex spin-container">
    <picture>
      <source srcset="/img/image.avif" type="image/avif" />
      <source srcset="/img/image.webp" type="image/webp" />
      <img src="/img/image.png" class="image" alt="background image" />
    </picture>
    <div
      class="icon"
      :class="{ 'disabled': spinClickCount >= MAX_SPIN_CLICKS }"
      @click="handleSpinClick"
      @keyup.enter="handleSpinClick"
      @keyup.space="handleSpinClick"
      v-tooltip.bottom="{
        value: hasActiveTicket || isDev ? `↻ Spin! (${spinClickCount}/${MAX_SPIN_CLICKS})` : `🔒 Scan tiket dulu`,
        class: 'text-xl',
        escape: true
      }"
      tabindex="0"
    ></div>
  </div>

  <!-- Spin Modal -->
  <SpinModal
    v-model:visible="showModal"
    :type="modalType"
    @scan-click="handleModalScanClick"
    @close="handleModalClose"
  />
</template>

<script setup lang="ts">
import { ref, onMounted, inject, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import random from 'random';
import { Wheel, type WheelProps } from 'spin-wheel';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import { TickSound, LabelLength } from '@/services/SettingService';
import { GroupLabel, GroupLabels, ItemService, Items } from '@/services/ItemService';
import { TicketService, currentTicket } from '@/services/TicketService';
import CongratulationDialog from '@/components/CongratulationDialog.vue';
import SpinModal from '@/components/SpinModal.vue';

// Define props
interface Props {
  showDropdown?: boolean;
}

withDefaults(defineProps<Props>(), {
  showDropdown: true // Default: show dropdown
});

const router = useRouter();
const itemService = inject<ItemService>('ItemService');
const ticketService = new TicketService();
const toast = useToast();

// Spin lock mechanism
const isSpinLocked = ref(false);
const hasSpunOnce = ref(false);
const isSpinning = ref(false); // Flag to prevent multiple spins
const spinClickCount = ref(0); // Counter untuk tracking jumlah klik spin
const MAX_SPIN_CLICKS = 3; // Maksimal 3 kali klik per ticket

// Modal state
const showModal = ref(false);
const modalType = ref<'no-ticket' | 'spin-complete' | 'spinning'>('no-ticket');

// Check if there's an active ticket
const hasActiveTicket = computed(() => currentTicket.value !== null);
const isSpecialTicket = computed(() => currentTicket.value?.type === 'special');

// Development mode check - DISABLED for proper validation testing
// Set to false to enforce ticket validation even in dev mode
const isDev = false; // process.env.NODE_ENV === 'development';

// Navigate to scanner
const goToScanner = () => {
  router.push('/scan');
};

// Modal handlers
const handleModalScanClick = () => {
  goToScanner();
};

const handleModalClose = () => {
  showModal.value = false;
};

const properties: WheelProps = {
  // debug: import.meta.env.DEV,
  isInteractive: false,
  radius: 0.48,
  rotationResistance: 0,
  itemLabelRadius: 0.92,
  itemLabelRadiusMax: 0.3,
  itemLabelRotation: 180,
  itemLabelAlign: 'left',
  itemLabelColors: ['#fff'],
  itemLabelBaselineOffset: -0.07,
  // Should also change app.scss
  itemLabelFont:
    '"Suez One", "Mochiy Pop P One", "Jua", "Unbounded", "Mitr", "Noto Sans TC", "Noto Sans SC", "Noto Sans Lao", "Noto Color Emoji"',
  itemLabelFontSizeMax: 55,
  itemBackgroundColors: [
    '#fdc963',
    '#00cca8',
    '#2b87e9',
    '#fd775b',
    '#ff4b78',
    '#c88857',
    '#a64a97',
    '#5b7c7d',
    '#715344',
    '#904e55',
    '#8b7856'
  ],
  rotationSpeedMax: 2000,
  lineWidth: 1,
  lineColor: '#fff',
  items: []
};

const container = ref();

let spinCount = 0;
let wheel: Wheel | undefined = undefined;

const stopAndClearSound = () => {
  if (!wheel) return;

  wheel.onCurrentIndexChange = () => {};
  wheel.stop();
};

const playSound = () => {
  if (!TickSound.value) return;

  var src = TickSound.value.value.startsWith('data:')
    ? TickSound.value.value
    : `/sound/${TickSound.value.value}`;
  const audio = new Audio(src);
  audio.volume = 0.3;
  audio.play();
};

// Handle spin button click - show modal if no ticket
const handleSpinClick = () => {
  console.log('handleSpinClick called', {
    isSpinning: isSpinning.value,
    hasActiveTicket: hasActiveTicket.value,
    isDev,
    currentTicket: currentTicket.value,
    spinClickCount: spinClickCount.value
  });

  // Check if already reached max clicks
  if (spinClickCount.value >= MAX_SPIN_CLICKS) {
    console.log('Max spin clicks reached:', spinClickCount.value);
    return;
  }

  // Check if no ticket (and not in dev mode)
  if (!hasActiveTicket.value && !isDev) {
    console.log('No ticket, showing modal');
    modalType.value = 'no-ticket';
    showModal.value = true;
    return; // IMPORTANT: Stop here, do NOT call spin()
  }

  // Increment spin click counter
  spinClickCount.value++;
  console.log('Spin click count:', spinClickCount.value);

  // Has ticket, allow spin (multiple times!)
  console.log('Has ticket, calling spin()');
  spin();
};

const spin = () => {
  console.log('spin() called', {
    wheel,
    isSpinning: isSpinning.value,
    isSpinLocked: isSpinLocked.value,
    hasActiveTicket: hasActiveTicket.value,
    currentSpeed: wheel?.rotationSpeed
  });

  if (!wheel) {
    console.log('No wheel, returning');
    return;
  }

  // Remove spinning checks - allow multiple spins!
  // User can click multiple times to add momentum

  console.log('Adding spin momentum...');
  
  // Set spinning flag (but don't block subsequent clicks)
  isSpinning.value = true;

  wheel.onCurrentIndexChange = () => {
    if (!wheel) return;

    playSound();

    // Change rotation resistance based on current speed.
    // Provide a more entertaining performance.
    switch (true) {
      case wheel.rotationSpeed < 400:
        wheel.rotationResistance = -100;
        break;
      case wheel.rotationSpeed < 100:
        wheel.rotationResistance = -30;
        break;
      case wheel.rotationSpeed < 30:
        wheel.rotationResistance = -10;
        break;
    }
  };

  wheel.rotationResistance = -400;

  // Check if this is the first spin (wheel not moving) and it's a special ticket
  const isFirstSpin = wheel.rotationSpeed === 0 || wheel.rotationSpeed < 10;
  
  // Jika special ticket dan ini spin pertama, tentukan target index
  if (isSpecialTicket.value && currentTicket.value?.specialPrizeId && isFirstSpin) {
    const specialPrizeIndex = Items.value?.findIndex(
      (item) => item._id === currentTicket.value?.specialPrizeId
    );

    if (specialPrizeIndex !== undefined && specialPrizeIndex !== -1) {
      // Spin ke item tertentu dengan animasi natural
      wheel.spinToItem(specialPrizeIndex, random.int(3000, 5000), true, 2, 1);
      console.log('Special ticket - first spin to prize:', specialPrizeIndex);
    } else {
      // Fallback jika special prize tidak ditemukan
      wheel.spin(wheel.rotationSpeed + random.int(1000, 1600));
    }
  } else {
    // Regular ticket, dev mode, atau subsequent spin - add momentum
    const additionalSpeed = random.int(1000, 1600);
    wheel.spin(wheel.rotationSpeed + additionalSpeed);
    console.log('Adding momentum:', additionalSpeed, 'Current speed:', wheel.rotationSpeed);
  }
};

const dialog = useDialog();
const openCongratulationDialog = async ($event: {
  type: 'rest';
  currentIndex: number;
  rotation: number;
}) => {
  // Tandai tiket sebagai used jika ada tiket aktif
  if (currentTicket.value && currentTicket.value.id) {
    try {
      await ticketService.markTicketAsUsed(currentTicket.value.id);
      console.log('Ticket marked as used:', currentTicket.value.code);

      // Clear current ticket setelah digunakan
      ticketService.clearCurrentTicket();

      toast.add({
        severity: 'info',
        summary: 'Tiket Digunakan',
        detail: 'Tiket Anda telah digunakan',
        life: 3000
      });
    } catch (error) {
      console.error('Error marking ticket as used:', error);
    }
  }

  dialog.open(CongratulationDialog, {
    props: {
      modal: true,
      showHeader: false,
      style: 'border: 0',
      contentStyle: 'border: 0; backgroundColor: transparent',
      dismissableMask: true
    },
    data: {
      item: Items.value![$event.currentIndex]
    }
  });
};

onMounted(() => {
  // Watch for ticket changes to reset spin click counter
  watch(currentTicket, (newTicket) => {
    if (newTicket) {
      // New ticket scanned, reset counter
      spinClickCount.value = 0;
      console.log('New ticket detected, reset spin click counter');
    }
  });

  watch(Items, (newValue) => (wheel!.items = newValue || []));
  watch(LabelLength, (newValue) => {
    wheel!.itemLabelRadiusMax = 1 - newValue;
  });

  wheel = new Wheel(container.value, {
    ...properties,
    items: Items.value,
    itemLabelRadiusMax: 1 - LabelLength.value
  });

  // Set random initial rotation so arrow points to random position
  const randomRotation = random.int(0, 360);
  wheel.rotation = randomRotation;
  console.log('Initial random rotation:', randomRotation);

  wheel.onRest = async ($event) => {
    stopAndClearSound;
    
    // Show congratulation dialog first - let user see the prize!
    await openCongratulationDialog($event);
    
    // After dialog closes, simply reset states
    // Ticket already cleared in openCongratulationDialog
    // User will see lock overlay and need to scan new ticket
    setTimeout(() => {
      isSpinning.value = false;
      isSpinLocked.value = false;
      hasSpunOnce.value = true;
      spinClickCount.value = 0; // Reset counter setelah wheel berhenti
      
      // Don't show modal - just let the lock overlay appear naturally
      // When user tries to spin again, handleSpinClick will show the modal
      
      console.log('Spin complete, ticket cleared, lock overlay active');
    }, 500);
  };

  wheel.onSpin = () => {
    gtag('event', 'spin');
    gtag('event', 'spin_count', {
      count: ++spinCount
    });
  };

  // Workaround for itemLabelRadiusMax not working on first load.
  setTimeout(() => {
    wheel!.itemLabelRadiusMax = 1 - LabelLength.value;
  }, 50);
});
</script>

<style lang="scss" scoped>
@import 'primeflex/core/_variables.scss';

.spin-container {
  aspect-ratio: 1/1;
  width: 200vw;
  height: 90vh;

  margin-top: -3.5rem;
  margin-bottom: -10vh;
  position: relative;

  @media (min-width: map-get($breakpoints, 'sm')) {
    height: 100vh;
  }

  @media (min-width: map-get($breakpoints, 'md')) {
    height: 110vh;
  }
}

.image {
  object-position: center;
  object-fit: contain;

  aspect-ratio: 1/1;
  width: 200vw;
  height: 90vh;

  position: absolute;
  top: calc(calc(50%) - calc(90vh / 2));
  left: calc(calc(50%) - calc(200vw / 2));

  @media (min-width: map-get($breakpoints, 'sm')) {
    height: 100vh;
    top: calc(calc(50%) - calc(100vh / 2));
  }

  @media (min-width: map-get($breakpoints, 'md')) {
    height: 110vh;
    top: calc(calc(50%) - calc(110vh / 2));
  }
}

.button-container {
  margin-top: -5.5rem;

  button {
    z-index: 2;
    position: relative;

    $background-color: #0c0f1d;
    background: $background-color;

    &:hover {
      filter: brightness(1.3);
    }
  }
}

.icon {
  $icon-size: 13vh;
  cursor: pointer;

  width: $icon-size;
  height: $icon-size;
  border-radius: 50%;

  background-image: url(/img/icon.png);
  background-image: -webkit-image-set(
    url(/img/icon.avif) type('image/avif'),
    url(/img/icon.webp) type('image/webp'),
    url(/img/icon.png) type('image/png')
  );
  background-image: image-set(
    url(/img/icon.avif) type('image/avif'),
    url(/img/icon.webp) type('image/webp'),
    url(/img/icon.png) type('image/png')
  );

  background-size: contain;

  position: absolute;
  top: calc(calc(50%) - calc($icon-size / 2));
  left: calc(calc(50%) - calc($icon-size / 2));

  &:hover {
    filter: brightness(1.1);
  }
}

// Lock Overlay
.lock-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease;

  .lock-icon-container {
    text-align: center;
    color: white;
    opacity: 0.8;
    animation: pulse 2s infinite;
  }
}

// Disabled State
.disabled {
  opacity: 0.5;
  pointer-events: none;
  filter: grayscale(50%);
}

// Locked State
.locked {
  position: relative;
  
  &::after {
    content: '🔒';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 3rem;
    opacity: 0.3;
    pointer-events: none;
  }
}

// Animations
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
}
</style>
